import React, { useState, ReactElement } from 'react';
import {
  GetWorkerDataDocument,
  useUpdateJobsiteWorkerMutation,
} from 'apollo/generated/client-operations';
import { AuthContext } from 'auth';
import { FormOnSubmit, ModalForm } from 'components/form';
import { AlertService } from 'components/alertService';
import { useIsMounted } from 'utils';
import { getGraphQLError } from 'utils/error';
import { to } from 'acl';
import { toFancySelectOptions } from 'utils/constants';
import { buildOptionsWithCurrentValue } from 'containers/worker/helpers/utils';
import { getJobsitesOptions } from './utils';
import { EditJobsiteWorkerModalProps } from './types';
import {
  getDefaultValues,
  getFormInputs,
  EditJobsiteWorkerFormData,
  getUpdateInput,
} from './EditJobsiteWorkerModal.forms';

export function EditJobsiteWorkerModal(
  props: EditJobsiteWorkerModalProps,
): ReactElement {
  const { isOpen, closeModal, onClosed, onConfirm, worker, jobsiteWorker } =
    props;

  const isMounted = useIsMounted();
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [isFormDirty, setIsFormDirty] = React.useState(false);
  const { currentUser: user } = React.useContext(AuthContext);

  const [updateJobsiteWorker] = useUpdateJobsiteWorkerMutation({
    fetchPolicy: 'no-cache',
    refetchQueries: [GetWorkerDataDocument],
  });

  const { jobsite } = jobsiteWorker?.jobsiteContractor ?? {};

  const {
    jobTitleOptions,
    tradeOptions,
    unionAffiliationOptions,
    classificationGroupOptions,
    jobsiteCustomWorkerDataFields,
  } = React.useMemo(() => {
    return {
      jobTitleOptions: toFancySelectOptions(
        buildOptionsWithCurrentValue(
          jobsite?.titleOptions,
          jobsiteWorker?.jobTitle,
        ),
      ),
      tradeOptions: toFancySelectOptions(
        buildOptionsWithCurrentValue(
          jobsite?.tradeOptions,
          jobsiteWorker?.trade,
        ),
      ),
      unionAffiliationOptions: toFancySelectOptions(
        buildOptionsWithCurrentValue(
          jobsite?.unionAffiliationOptions,
          jobsiteWorker?.unionAffiliation,
        ),
      ),
      classificationGroupOptions: toFancySelectOptions(
        buildOptionsWithCurrentValue(
          jobsite?.classificationGroupOptions,
          jobsiteWorker?.classificationGroup,
        ),
      ),
      jobsiteCustomWorkerDataFields:
        jobsite?.jobsiteCustomWorkerDataFields?.edges?.map((e) => e.node) ?? [],
    };
  }, [jobsite, jobsiteWorker]);

  const canAddUnions = user.isAllowed(to.addUnions);
  const jobsitesOptions = React.useMemo(
    () => getJobsitesOptions([jobsite].filter(Boolean)),
    [jobsite],
  );
  const formInputs = React.useMemo(
    () =>
      getFormInputs({
        jobsitesOptions,
        user,
        canAddUnions,
        tradeOptions,
        jobTitleOptions,
        unionAffiliationOptions,
        classificationGroupOptions,
        jobsiteCustomWorkerDataFields,
      }),
    [
      jobsitesOptions,
      user,
      canAddUnions,
      tradeOptions,
      jobTitleOptions,
      unionAffiliationOptions,
      classificationGroupOptions,
      jobsiteCustomWorkerDataFields,
    ],
  );
  const defaultValues = React.useMemo(
    () =>
      getDefaultValues({
        worker,
        jobsiteWorker,
        jobsitesOptions,
        tradeOptions,
        jobTitleOptions,
        unionAffiliationOptions,
        classificationGroupOptions,
        jobsiteCustomWorkerDataFields,
      }),
    [
      worker,
      jobsiteWorker,
      jobsitesOptions,
      tradeOptions,
      jobTitleOptions,
      unionAffiliationOptions,
      classificationGroupOptions,
      jobsiteCustomWorkerDataFields,
    ],
  );

  const onSubmit: FormOnSubmit<EditJobsiteWorkerFormData> = async (
    data,
    event,
    dirtyFields,
  ): Promise<void> => {
    if (isFetching) {
      return;
    }

    setIsFetching(true);
    const input = getUpdateInput(
      jobsiteWorker,
      data,
      dirtyFields,
      jobsiteCustomWorkerDataFields,
    );
    try {
      await updateJobsiteWorker({ variables: input });

      if (isMounted()) {
        onConfirm();
      }
      AlertService.alert(
        'success',
        'Success',
        'Worker data successfully updated',
      );
    } catch (error) {
      event.preventDefault();
      AlertService.alert(
        'danger',
        'Something went wrong!',
        getGraphQLError(error),
      );
    } finally {
      if (isMounted()) {
        setIsFetching(false);
      }
    }
  };

  return (
    <ModalForm
      open={isOpen}
      setOpen={closeModal}
      afterLeave={onClosed}
      onIsDirtyChange={setIsFormDirty}
      title="Update worker data"
      inputs={formInputs}
      onSubmit={onSubmit}
      defaultValues={defaultValues}
      inputsContainerClassName="odin-grid odin-grid-cols-6 odin-gap-6"
      actionText="Save Changes"
      actionButtonEnabled={isFormDirty}
      actionButtonWithSpinner={isFetching}
    />
  );
}
